import { Card, type TablePaginationConfig } from "antd"
import type { FilterValue, SorterResult } from "antd/es/table/interface"
import type { BaseOptionType } from "antd/lib/select"
import React, { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"

import { EntityTable } from "../../../../../../../components/EntityTable/EntityTable.tsx"
import {
  DEFAULT_TAB,
  TABLE_SURFACES
} from "../../../../../../../components/EntityTable/surfaces.ts"
import { TableAddAction } from "../../../../../../../components/Table/TableAddAction.tsx"
import { TableResultCount } from "../../../../../../../components/Table/TableResultCount.tsx"
import { useScrolledTable } from "../../../../../../../hooks/use-scrolled-table.ts"
import { cn } from "../../../../../../../utils/cn.ts"
import { getTableOrderingFromSorter } from "../../../../../../../utils/table"
import { type PermissionProjectPermissions } from "../../../../../../SettingsPermissions/types/permissions.types.ts"
import { type ProjectInvoiceFilters } from "../../../../../redux/project.types.ts"
import { type ProjectInvoice } from "../../../../../types/project-invoices.types.tsx"
import {
  INVOICE_TYPES,
  isConcreteInvoiceType
} from "../../../../constants/invoice-statuses.ts"
import { ProjectViewInvoiceNewRow } from "./ProjectViewInvoiceNewRow.tsx"
import { getColumns } from "./columns.tsx"
import { getInvoicePermissions } from "./get-invoice-permissions.tsx"

const NEW_ROW_ID = "__new_invoice__"

interface RowProps
  extends React.DetailedHTMLProps<
      React.HTMLAttributes<HTMLTableRowElement>,
      HTMLTableRowElement
    >,
    Pick<ProjectViewInvoicesListProps, "onSelectRow"> {
  record: ProjectInvoice
  options?: Record<string, BaseOptionType[]>
  selfAssigned?: boolean
  editableRow?: string
  isAdding?: boolean
}

interface ProjectViewInvoicesListProps {
  userId: string
  invoices: ProjectInvoice[]
  fetching: boolean
  projectId: string
  selfAssigned: boolean
  next?: string | null
  /** The page a load-more failed on, offered back. */
  retryNext?: string | null
  count: number
  type: ProjectInvoiceFilters["type"]
  editableRow?: string
  isAdding?: boolean
  projectPermissions: PermissionProjectPermissions
  voidAllowed?: boolean
  options?: Record<string, BaseOptionType[]>
  onOrderingChange: (ordering: string | null) => void
  onSelectRow: (record: ProjectInvoice, action: string) => void
  onPageChange: (page: number) => void
  onLoadMore: () => void
  onAddInvoice: () => void
  // todo is a temporary solution and will need to be removed!
  uploadFileAllowed?: boolean
  deleteForeignFileAllowed?: boolean
  deleteFileAllowed?: boolean
  // False only for a tour/demo preview reusing this real table on fake data —
  // its column edits stay local instead of overwriting every real project's
  // saved Invoices layout, which is keyed by this same surface for every
  // account. Defaults to true so every real caller is unaffected.
  persistColumnConfig?: boolean
}

export function ProjectViewInvoicesList({
  userId,
  invoices,
  projectId,
  selfAssigned,
  next,
  retryNext,
  count,
  type,
  fetching,
  projectPermissions,
  voidAllowed,
  editableRow = "",
  isAdding = false,
  options = {},
  uploadFileAllowed,
  deleteForeignFileAllowed,
  deleteFileAllowed,
  onOrderingChange,
  onSelectRow = () => {},
  onPageChange,
  onLoadMore,
  onAddInvoice,
  persistColumnConfig = true
}: ProjectViewInvoicesListProps) {
  const { t } = useTranslation()
  const moduleAccess = projectPermissions?.invoices
  //dataforest.atlassian.net/browse/EZB-2836
  const isLocked = !!editableRow || isAdding
  const createAllowed = moduleAccess?.project_invoice_create
  const deleteAllowed = moduleAccess?.project_invoice_delete
  const deleteForeignAllowed =
    deleteAllowed && moduleAccess?.project_invoice_manage_others
  const editAllowed = moduleAccess?.project_invoice_edit
  const editForeignAllowed =
    editAllowed && moduleAccess?.project_invoice_manage_others

  const tableData = useMemo(() => {
    const results = invoices || []

    if (isAdding) {
      const newInvoice = {
        id: NEW_ROW_ID,
        type: isConcreteInvoiceType(type) ? type : INVOICE_TYPES.RECEIVABLE,
        ...(selfAssigned ? { assignee: userId } : {})
      } as ProjectInvoice

      return [newInvoice, ...results] as ProjectInvoice[]
    }

    return results
  }, [invoices, type, isAdding, selfAssigned, userId])

  const columns = useMemo(
    () =>
      getColumns({
        t,
        currentUserId: userId,
        createAllowed,
        deleteAllowed,
        deleteForeignAllowed,
        editAllowed,
        editForeignAllowed,
        voidAllowed,
        //
        uploadFileAllowed,
        deleteForeignFileAllowed,
        deleteFileAllowed,
        //
        projectId,
        isLocked,
        onSelectRow
      }),
    [
      t,
      onSelectRow,
      userId,
      projectId,
      isLocked,
      createAllowed,
      deleteAllowed,
      deleteForeignAllowed,
      editAllowed,
      editForeignAllowed,
      voidAllowed,
      uploadFileAllowed,
      deleteForeignFileAllowed,
      deleteFileAllowed
    ]
  )

  const onChangeTable = useCallback(
    (
      pagination: TablePaginationConfig,
      filters: Record<string, FilterValue | null>,
      sorter: SorterResult<ProjectInvoice> | SorterResult<ProjectInvoice>[]
    ) => {
      const ordering = getTableOrderingFromSorter(sorter)

      onPageChange(pagination.current || 1)
      onOrderingChange(ordering)
    },
    [onPageChange, onOrderingChange]
  )

  const RowComponent = useCallback((props: RowProps) => {
    const {
      record,
      options: rowOptions,
      selfAssigned: rowSelfAssigned,
      editableRow: rowEditableRow,
      isAdding: rowIsAdding,
      onSelectRow: rowOnSelectRow,
      ...rest
    } = props

    const isNewRow = record?.id === NEW_ROW_ID && !!rowIsAdding
    const isEditRow = record?.id === rowEditableRow && !!rowEditableRow

    if (isNewRow || isEditRow) {
      return (
        <ProjectViewInvoiceNewRow
          record={record}
          options={rowOptions ?? {}}
          mode={isNewRow ? "create" : "edit"}
          selfAssigned={!!rowSelfAssigned}
          onSelectRow={rowOnSelectRow}
          {...rest}
        />
      )
    }

    return <tr {...rest}>{rest?.children}</tr>
  }, [])

  const { tableRef, tableHeight, onTableScroll } = useScrolledTable({
    loading: fetching,
    next: next,
    loadMore: onLoadMore,
    disabled: isLocked
  })

  const getRowProps = useCallback(
    (record: ProjectInvoice) => {
      const { canEdit } = getInvoicePermissions({
        record,
        currentUserId: userId,
        deleteInvoice: deleteAllowed,
        deleteForeignInvoice: deleteForeignAllowed,
        editInvoice: editAllowed,
        editForeignInvoice: editForeignAllowed
      })

      return {
        record,
        onSelectRow,
        options,
        selfAssigned,
        editableRow,
        isAdding,
        onDoubleClick: canEdit
          ? () => !isLocked && onSelectRow(record, "edit_row")
          : undefined
      }
    },
    [
      userId,
      deleteAllowed,
      deleteForeignAllowed,
      editAllowed,
      editForeignAllowed,
      isLocked,
      onSelectRow,
      options,
      selfAssigned,
      editableRow,
      isAdding
    ]
  )

  const tableComponents = useMemo(
    () => ({
      body:
        tableData?.length &&
        (editAllowed || editForeignAllowed || createAllowed)
          ? { row: RowComponent }
          : {}
    }),
    [
      tableData?.length,
      editAllowed,
      editForeignAllowed,
      createAllowed,
      RowComponent
    ]
  )

  return (
    <TableAddAction
      label={t("table.addRow.invoice")}
      allowed={!!createAllowed}
      disabled={isLocked}
      onAdd={onAddInvoice}
    >
      <Card classNames={{ body: "p-0" }} ref={tableRef}>
        <EntityTable<ProjectInvoice>
          surface={TABLE_SURFACES.projectInvoices}
          persistColumnConfig={persistColumnConfig}
          selectionMode="on-demand"
          tab={DEFAULT_TAB}
          rowKey="id"
          size="small"
          rowHoverable={false}
          onScroll={onTableScroll}
          className={cn(
            "[&_th]:bg-primary [&_th]:rounded-none [&_th]:text-white",
            {
              "[&_.ant-table-cell-scrollbar]:w-0 [&_.ant-table-cell-scrollbar]:p-1":
                !invoices?.length
            }
          )}
          columns={columns}
          loading={fetching && !invoices?.length}
          dataSource={tableData}
          scroll={{
            x: 1300,
            y: tableHeight,
            scrollToFirstRowOnChange: true
          }}
          onChange={onChangeTable}
          withOutPagination
          pagination={false}
          onRow={getRowProps}
          components={tableComponents}
        />
        <TableResultCount
          underAddAction
          loaded={invoices?.length ?? 0}
          total={count}
          loading={fetching}
          next={next}
          retryNext={retryNext}
          onRetry={onLoadMore}
        />
      </Card>
    </TableAddAction>
  )
}

export default ProjectViewInvoicesList
